import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import {
  BellIcon,
  ChevronDownIcon,
  LogoutIcon,
  QuestionMarkIcon,
  UserCircleIcon,
} from '../../assets/svg/SVGComponents';

import { useAuth } from '../../hooks/auth';

import Dropdown, { DropdownHandles } from '../Dropdown';

import {
  Container,
  SearchBar,
  Content,
  Profile,
  DropdownPerfilContainer,
  UserAvatar,
  DropdownNotificationContainer,
  NotificationIconWrapper,
  NotificationBadge,
} from './styles';

import avatar_api_letters from '../../services/avatarLetters';
import NotificationPanel from '../NotificationPanel';
import { useNotification } from '../../hooks/notifications';
import api from '../../services/api';
import { useOnboarding, TutorialId } from '../../hooks/onboarding';

const Header: React.FC = () => {
  const userDropdownRef = useRef<DropdownHandles>(null);
  const perfilRefDropdown = useRef<HTMLDivElement>(null);
  const notificationDropdownRef = useRef<DropdownHandles>(null);
  const NotificationRefdropdown = useRef<HTMLDivElement>(null);
  const { notifications, unreadCount, markAllAsRead } = useNotification();
  const [isAnimating, setIsAnimating] = useState(false);
  const { user, signOut } = useAuth();
  const { t } = useTranslation();
  const { startOnboarding, startTutorial, isOnboardingActive } =
    useOnboarding();
  const location = useLocation();

  const handleMarkNotificationsAsRead = useCallback(async () => {
    if (unreadCount <= 0) return;

    const unreadIds = notifications
      .filter(notification => !notification.read)
      .map(notification => notification.id);

    if (unreadIds.length === 0) return;

    // Marca como lido localmente imediatamente (otimista)
    markAllAsRead();

    try {
      await api.patch('/notifications/read', {
        notifications_id: unreadIds,
      });
    } catch {
      // Falha silenciosa — estado local já foi atualizado
    }
  }, [markAllAsRead, notifications, unreadCount]);

  // Ao clicar no perfil do usuário verifica se o menu de notificações está aberto
  const handlePerfilDropdownClick = useCallback(() => {
    if (notificationDropdownRef.current?.isOpen()) {
      handleMarkNotificationsAsRead();
      notificationDropdownRef.current?.closeDropdown();
    }

    userDropdownRef.current?.toggleDropdown();
  }, [handleMarkNotificationsAsRead]);

  // Ao clicar na notificações verifica se o menu de notificações está aberto
  const handleNotificationDropdownClick = useCallback(() => {
    if (userDropdownRef.current?.isOpen()) {
      userDropdownRef.current?.closeDropdown();
    }

    const isCurrentlyOpen = notificationDropdownRef.current?.isOpen();

    if (isCurrentlyOpen) {
      // Fechando o painel → marcar como lido
      handleMarkNotificationsAsRead();
    }

    notificationDropdownRef.current?.toggleDropdown();

    if (!isCurrentlyOpen) {
      // Abrindo o painel → marcar como lido após abrir
      handleMarkNotificationsAsRead();
    }
  }, [handleMarkNotificationsAsRead]);

  // Faz o logoff do usuário
  const handleLogoutClick = useCallback(() => {
    userDropdownRef.current?.toggleDropdown();
    signOut();
  }, [signOut]);

  const handleStartTour = useCallback(() => {
    const { pathname } = location;

    if (pathname.startsWith('/hardwares/profile')) {
      startTutorial(TutorialId.TOOLS_PROFILE);
    } else if (pathname.startsWith('/hardwares')) {
      startTutorial(TutorialId.TOOLS);
    } else if (pathname.startsWith('/profile')) {
      startTutorial(TutorialId.PROFILE);
    } else if (pathname.startsWith('/videos')) {
      startTutorial(TutorialId.VIDEOS);
    } else if (/\/projects\/[^/]+\/[^/]+\/activity\//.test(pathname)) {
      startTutorial(TutorialId.ACTIVITIES);
    } else if (
      pathname.startsWith('/projects') ||
      pathname.startsWith('/trails')
    ) {
      startTutorial(TutorialId.PROJECTS);
    } else if (pathname.startsWith('/community')) {
      startTutorial(TutorialId.COMMUNITY);
    } else if (pathname.startsWith('/challenges')) {
      startTutorial(TutorialId.CHALLENGES);
    } else if (pathname.startsWith('/home')) {
      startTutorial(TutorialId.HOME);
    } else {
      startOnboarding();
    }
  }, [location, startTutorial, startOnboarding]);

  const clickListener = useCallback(
    (e: MouseEvent) => {
      if (
        !perfilRefDropdown ||
        !perfilRefDropdown?.current?.contains(e.target as Node)
      ) {
        userDropdownRef.current?.closeDropdown();
      }

      if (
        !NotificationRefdropdown ||
        !NotificationRefdropdown?.current?.contains(e.target as Node)
      ) {
        if (notificationDropdownRef.current?.isOpen()) {
          handleMarkNotificationsAsRead();
        }
        notificationDropdownRef.current?.closeDropdown();
      }
    },
    [handleMarkNotificationsAsRead],
  );

  useEffect(() => {
    if (unreadCount > 0) {
      setIsAnimating(true);
      const timer = setTimeout(() => {
        setIsAnimating(false);
      }, 1000);

      return () => clearTimeout(timer);
    }
    return () => {
      setIsAnimating(false);
    };
  }, [unreadCount]);

  useEffect(() => {
    // Attach the listeners on component mount.
    document.addEventListener('mousedown', clickListener);

    // Detach the listeners on component unmount.
    return () => {
      document.removeEventListener('mousedown', clickListener);
    };
  }, [clickListener, perfilRefDropdown]);

  return (
    <Container>
      <Content>
        <SearchBar />

        <Profile>
          <DropdownNotificationContainer ref={NotificationRefdropdown}>
            <button type="button" onClick={handleNotificationDropdownClick}>
              <NotificationIconWrapper className={isAnimating ? 'animate' : ''}>
                <BellIcon strokeWidth={2} stroke="#323232" width="1.8rem" />
                {unreadCount > 0 && (
                  <NotificationBadge>{unreadCount}</NotificationBadge>
                )}
              </NotificationIconWrapper>
            </button>

            <Dropdown ref={notificationDropdownRef}>
              <NotificationPanel />
            </Dropdown>
          </DropdownNotificationContainer>

          <DropdownPerfilContainer
            ref={perfilRefDropdown}
            className="perfil-dropdown"
          >
            <button type="button" onClick={handlePerfilDropdownClick}>
              <UserAvatar
                $avatarSrc={user.avatar_url || avatar_api_letters(user.name)}
              />
              <ChevronDownIcon strokeWidth={3} stroke="#323232" width="0.8em" />
            </button>

            <Dropdown ref={userDropdownRef}>
              <ul>
                <li>
                  <Link to="/profile" onClick={handlePerfilDropdownClick}>
                    <UserCircleIcon
                      width="1.44rem"
                      stroke="#323232"
                      strokeWidth={1.5}
                      fill="none"
                    />
                    {t('header.my_profile')}
                  </Link>
                </li>
                <li>
                  <button type="button" onClick={handleStartTour}>
                    <QuestionMarkIcon
                      width="1.44rem"
                      stroke="#323232"
                      strokeWidth={1.5}
                      fill="none"
                    />
                    {t('buttons.start_tour')}
                  </button>
                </li>
                <li>
                  <Link
                    to="/"
                    style={{ color: '#FF9800' }}
                    onClick={handleLogoutClick}
                  >
                    <LogoutIcon
                      width="1.44rem"
                      stroke="#FF9800"
                      strokeWidth={1.5}
                      fill="none"
                    />
                    {t('header.signout')}
                  </Link>
                </li>
              </ul>
            </Dropdown>
          </DropdownPerfilContainer>
        </Profile>
      </Content>
    </Container>
  );
};

export default Header;
